import {
  CheckedValue,
  LogisticType,
  OrderData,
  OrderFields,
  OrderFormData,
  OrderTypeId,
  ResolutionId,
  isOrderTypeWithoutTsOwnerPhoneFields,
} from 'types/order'
import { EFFECTIVE_ORDER_STATUSES } from '../constants/order'

const conditionallyAppendTexts = (conditionsAndTexts: [boolean, string][]): string =>
  conditionsAndTexts.reduce((result, [condition, text]) => (condition ? `${result}${text}` : result), '')

const doesOrderHaveOneOfTypes = (order: OrderData | OrderFormData, types: OrderTypeId[]) =>
  types.includes(String(order[OrderFields.ORDER_TYPE]) as OrderTypeId)

const doesOrderHaveLogisticFields = (order: OrderData | OrderFormData): boolean =>
  doesOrderHaveOneOfTypes(order, [
    OrderTypeId.LOGISTIC_AND_DRIVER,
    OrderTypeId.LOGISTIC_PAYER,
    OrderTypeId.LOGISTIC_CONTRACTOR,
    OrderTypeId.LOGISTIC,
    OrderTypeId.COUNTERPARTY_BORROWER_AND_TS,
  ])

const doesOrderHaveDriverFields = (order: OrderData | OrderFormData): boolean =>
  doesOrderHaveOneOfTypes(order, [
    OrderTypeId.LOGISTIC_AND_DRIVER,
    OrderTypeId.DRIVER,
    OrderTypeId.DRIVER_FOR_LOADING,
    OrderTypeId.DRIVER_AND_TS,
    OrderTypeId.DRIVER_DOC_CHECK,
  ])

const doesOrderHaveStateDriverFields = (order: OrderData | OrderFormData): boolean => doesOrderHaveOneOfTypes(order, [OrderTypeId.STATE_DRIVER])

const doesOrderHaveEmployeeFields = (order: OrderData | OrderFormData): boolean => doesOrderHaveOneOfTypes(order, [OrderTypeId.EMPLOYEE])

const doesOrderHaveTsFields = (order: OrderData | OrderFormData): boolean =>
  doesOrderHaveOneOfTypes(order, [
    OrderTypeId.LOGISTIC_AND_DRIVER,
    OrderTypeId.DRIVER_AND_TS,
    OrderTypeId.TS,
    OrderTypeId.COUNTERPARTY_BORROWER_AND_TS,
  ])

const getLogisticTexts = (order: OrderData | OrderFormData): string =>
  conditionallyAppendTexts([
    [!order[OrderFields.LOGISTIC_FIO] || !order[OrderFields.LOGISTIC_TEL], `\n - Контакт перевозчика (${order[OrderFields.LOGISTIC_FIO]})`],
  ])

const getDriverTexts = (order: OrderData | OrderFormData): string =>
  conditionallyAppendTexts([
    [!order[OrderFields.DRIVER_BITH] || !order[OrderFields.DRIVER_SER_NUM], '\n - Паспорт водителя'],
    [!order[OrderFields.DRIVER_DOC] || !order[OrderFields.DRIVER_DOC_DATE], '\n - ВУ водителя'],
    [!order[OrderFields.DRIVER_TEL_1], '\n - Телефон водителя'],
  ])

const getStateDriverTexts = (order: OrderData | OrderFormData): string =>
  conditionallyAppendTexts([
    [!order[OrderFields.DRIVER_BITH] || !order[OrderFields.DRIVER_SER_NUM], '\n - Паспорт водителя'],
    [!order[OrderFields.DRIVER_DOC] || !order[OrderFields.DRIVER_DOC_DATE], '\n - ВУ водителя'],
    [!order[OrderFields.DRIVER_TEL_1], '\n - Телефон водителя'],
  ])

const getEmployeeTexts = (order: OrderData | OrderFormData): string =>
  conditionallyAppendTexts([
    [!order[OrderFields.DRIVER_BITH] || !order[OrderFields.DRIVER_SER_NUM], '\n - Паспорт сотрудника'],
    [!order[OrderFields.DRIVER_TEL_1], '\n - Телефон сотрудника'],
  ])

const getTsTexts = (order: OrderData | OrderFormData): string => {
  const withoutTsOwnerPhones = isOrderTypeWithoutTsOwnerPhoneFields(order[OrderFields.ORDER_TYPE])

  return conditionallyAppendTexts([
    [!order[OrderFields.TS_OWNER] || !order[OrderFields.TRANSPORT_STS_DATE], '\n - СТС сторону собственника тягача'],
    [
      !withoutTsOwnerPhones && !!order[OrderFields.TS_OWNER] && !order[OrderFields.TS_OWNER_TEL],
      `\n - Телефон собственника тягача (${order[OrderFields.TS_OWNER]})`,
    ],
    [!order[OrderFields.TS_OWNER_LICENSE_PLATE], '\n - СТС сторону госномера тягача'],
    [!order[OrderFields.TRANSPORT_YEAR], '\n - СТС сторону госномера тягача'],

    [
      (!order[OrderFields.TRAILER_STATUS] && !order[OrderFields.TRAILER_OWNER]) ||
        (!order[OrderFields.TRAILER_STATUS] && !order[OrderFields.TRAILER_STS_DATE]),
      '\n - СТС сторону собственника прицепа',
    ],
    [
      (!order[OrderFields.TRAILER_STATUS] && !order[OrderFields.TRAILER_OWNER_LICENSE_PLATE]) ||
        (!order[OrderFields.TRAILER_STATUS] && !order[OrderFields.TRAILER_YEAR]),
      '\n - СТС сторону госномера прицепа',
    ],
    [
      !withoutTsOwnerPhones && !order[OrderFields.TRAILER_STATUS] && !!order[OrderFields.TRAILER_OWNER] && !order[OrderFields.TRAILER_OWNER_TEL],
      `\n - Телефон собственника прицепа (${order[OrderFields.TRAILER_OWNER]})`,
    ],
  ])
}

export const getTextOfDataRequestTemplate = (order: OrderData | OrderFormData): string =>
  conditionallyAppendTexts([
    [doesOrderHaveLogisticFields(order), getLogisticTexts(order)],
    [doesOrderHaveDriverFields(order), getDriverTexts(order)],
    [doesOrderHaveStateDriverFields(order), getStateDriverTexts(order)],
    [doesOrderHaveEmployeeFields(order), getEmployeeTexts(order)],
    [doesOrderHaveTsFields(order), getTsTexts(order)],
  ])

export const getCompanyCheckHistoryField = (logisticType: OrderData[typeof OrderFields.LOGISTIC_TYPE]) =>
  logisticType === LogisticType.FOREIGN ? OrderFields.LOGISTIC_INN : OrderFields.LOGISTIC_OGRN

export const getCheckedValueByType = (checkedValue: CheckedValue[], type: string) =>
  checkedValue.find((check) => check.field === type) || { field: type, data: [] }

export const getLastOrderFromCheckedValue = (checkedValue: CheckedValue | undefined) => {
  const pastEffectiveOrders =
    checkedValue?.data?.filter((check) => EFFECTIVE_ORDER_STATUSES.includes(String(check.resolution_id) as ResolutionId)) || []
  return pastEffectiveOrders.sort((first, second) => (new Date(first.date_create_cus) > new Date(second.date_create_cus) ? -1 : 1))[0]
}

export const getResolutionsForExpertByOrderType = (type: OrderTypeId): ResolutionId[] => {
  switch (String(type) as OrderTypeId) {
    case OrderTypeId.LOGISTIC_AND_DRIVER:
      return [
        ResolutionId.SHIPMENT_ALLOWED,
        ResolutionId.SHIPMENT_FORBIDDEN,
        ResolutionId.LEGAL_ENTITY_PERMITTED_DRIVER_FORBIDDEN,
        ResolutionId.CLARIFY_DATA,
        ResolutionId.L1_PROCESSING,
      ]

    case OrderTypeId.DRIVER_AND_TS:
    case OrderTypeId.DRIVER_FOR_LOADING:
      return [ResolutionId.SHIPMENT_ALLOWED, ResolutionId.SHIPMENT_FORBIDDEN, ResolutionId.CLARIFY_DATA, ResolutionId.L1_PROCESSING]

    case OrderTypeId.EMPLOYEE:
    case OrderTypeId.STATE_DRIVER:
      return [ResolutionId.COOPERATION_AGREED, ResolutionId.COOPERATION_FORBIDDEN, ResolutionId.CLARIFY_DATA, ResolutionId.L1_PROCESSING]

    case OrderTypeId.TS:
    case OrderTypeId.LOGISTIC_CONTRACTOR:
      return [ResolutionId.SHIPMENT_ALLOWED, ResolutionId.SHIPMENT_FORBIDDEN, ResolutionId.CLARIFY_DATA, ResolutionId.L1_PROCESSING]

    case OrderTypeId.DRIVER_DOC_CHECK:
    case OrderTypeId.LOGISTIC_PAYER:
    case OrderTypeId.COUNTERPARTY_BORROWER_AND_TS:
    case OrderTypeId.PHONE_CHECK:
      return [ResolutionId.COOPERATION_AGREED, ResolutionId.COOPERATION_FORBIDDEN, ResolutionId.CLARIFY_DATA, ResolutionId.L1_PROCESSING]

    default:
      return [
        ResolutionId.SHIPMENT_ALLOWED,
        ResolutionId.SHIPMENT_FORBIDDEN,
        ResolutionId.COOPERATION_AGREED,
        ResolutionId.COOPERATION_FORBIDDEN,
        ResolutionId.LEGAL_ENTITY_PERMITTED_DRIVER_FORBIDDEN,
        ResolutionId.CLARIFY_DATA,
        ResolutionId.L1_PROCESSING,
      ]
  }
}
